import { PerspectiveAttribute, PerspectiveValueSource } from '@the-real-insight/tri-model';

import { FieldChange } from './FieldHistory';

/**
 * How much a figure is to be trusted, in three states.
 *
 * A perspective mixes three kinds of number that look identical once written down: one somebody
 * typed, one that came out of the data, and one the activation estimated because nothing better was
 * available. The third is the dangerous one — a plot size of 500 m² that was never measured reads
 * exactly like one that was, and every ratio computed from it inherits the guess in silence.
 *
 * So the state is shown rather than the provenance: green for a figure that stands, amber for one
 * that was worked out, red for one that is not there at all. What separates them:
 *
 *  - **missing** — nothing is stored. Not an error; most fields start here.
 *  - **estimated** — either the model declares the attribute derived (it is computed by definition),
 *    or the last change to it was written by the system. An estimate that a person then overwrites
 *    stops being an estimate, which is why the *last* change decides and not the first.
 *  - **certain** — everything else: typed by somebody, or delivered by the data with no change
 *    recorded at all. Both are as good as this feature can tell, and pretending to distinguish them
 *    would be inventing a difference the record does not hold.
 */

export type ValueConfidence = 'certain' | 'estimated' | 'missing';

const isEmpty = (value: any): boolean =>
    value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);

/** The last thing that happened to this field, or undefined where nothing did. */
export const lastChangeOf = (changes: FieldChange[] | undefined, name: string): FieldChange | undefined =>
    (changes ?? []).filter((change) => change.field === name).slice(-1)[0];

export const confidenceOf = (
    attribute: Pick<PerspectiveAttribute, 'name' | 'source'>,
    value: any,
    changes: FieldChange[] | undefined,
): ValueConfidence => {
    if (isEmpty(value)) return 'missing';

    // Derived by declaration: a ratio the estimator works out is an estimate however it was
    // arrived at, and it stays one until the model says otherwise.
    if (attribute.source === PerspectiveValueSource.derived) return 'estimated';

    return lastChangeOf(changes, attribute.name)?.type === 'system' ? 'estimated' : 'certain';
};
